import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography, TextField, Button, IconButton, FormControl, InputLabel, Select, MenuItem, FormHelperText, Divider, useTheme, useMediaQuery, Alert } from '@mui/material';
import { XIcon } from 'lucide-react';
interface UserFormProps {
  open: boolean;
  user: any | null;
  onClose: () => void;
  onSave: (userData: any) => void;
}
interface FormData {
  fullName: string;
  username: string;
  email: string;
  mobile: string;
  branchCode: string;
  settlementAccount: string;
  designation: string;
}
interface FormErrors {
  fullName?: string;
  username?: string;
  email?: string;
  mobile?: string;
  branchCode?: string;
  settlementAccount?: string;
  designation?: string;
}
const BRANCH_CODES = ['LHR001', 'LHR002', 'KHI001', 'KHI002', 'ISB001', 'ISB002', 'FSD001', 'MLT001'];
// Simulated OTP hashing function (in production, this would be done server-side)
const hashOTP = (otp: string): string => {
  // This is a placeholder - in production, use bcrypt or similar
  return `hashed_${otp}_${Date.now()}`;
};
const UserFormDrawer: React.FC<UserFormProps> = ({
  open,
  user,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    username: '',
    email: '',
    mobile: '',
    branchCode: '',
    settlementAccount: '',
    designation: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showOTPInfo, setShowOTPInfo] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName || '',
        username: user.username || '',
        email: user.email || '',
        mobile: user.mobile || '',
        branchCode: user.branchCode || '',
        settlementAccount: user.settlementAccount || '',
        designation: user.designation || ''
      });
      setShowOTPInfo(false);
    } else {
      setFormData({
        fullName: '',
        username: '',
        email: '',
        mobile: '',
        branchCode: '',
        settlementAccount: '',
        designation: ''
      });
      setShowOTPInfo(true);
    }
    setErrors({});
  }, [user, open]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | {
    name?: string;
    value: unknown;
  }>) => {
    const {
      name,
      value
    } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value
      });
      // Clear error when field is edited
      if (errors[name as keyof FormErrors]) {
        setErrors({
          ...errors,
          [name]: undefined
        });
      }
    }
  };
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (!/^[a-z0-9.]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain lowercase letters, numbers, and dots';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\+92\d{10}$/.test(formData.mobile.replace(/\s/g, ''))) {
      newErrors.mobile = 'Mobile must start with +92 and be exactly 10 digits after +92';
    }
    // Optional fields - no validation required
    // branchCode, settlementAccount, and designation can be empty
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (validate()) {
      const userData = {
        ...formData,
        id: user?.id
      };
      // If creating a new user, generate OTP
      if (!user) {
        const tempOTP = 'Test01';
        const hashedOTP = hashOTP(tempOTP);
        userData.hashedPassword = hashedOTP;
        userData.firstLoginRequired = true;
        userData.tempOTP = tempOTP; // For display purposes only
      }
      onSave(userData);
    }
  };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: {
        xs: '100%',
        sm: 400
      },
      p: 0
    }
  }} data-id="element-2886" data-mp="2hy2d0ipu5ft8">
      <Box sx={{
      p: 3
    }} data-id="element-2887" data-mp="2mvueo4ga8gam">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }} data-id="element-2888" data-mp="2mvcdcterjbvu">
          <Typography variant="h6" data-id="element-2889" data-mp="2wv336hsdp2pl">
            {user ? 'Edit User' : 'Add New User'}
          </Typography>
          <IconButton onClick={onClose} size="small" data-id="element-2890" data-mp="16idsdy020svl">
            <XIcon size={18} data-id="element-2891" data-mp="12gacmx6cr24i" />
          </IconButton>
        </Box>
        {showOTPInfo && <Alert severity="info" sx={{
        mb: 2
      }} data-id="element-2892" data-mp="scc1zpxij9kr">
            A temporary password (OTP: Test01) will be generated. User must
            reset password on first login.
          </Alert>}
        <Box component="form" noValidate autoComplete="off" sx={{
        mt: 2
      }} data-id="element-2893" data-mp="ilr3e2hua51u">
          <TextField fullWidth margin="normal" label="Full Name" name="fullName" value={formData.fullName} onChange={handleChange} error={!!errors.fullName} helperText={errors.fullName} required data-id="element-2894" data-mp="8oct6kmiechz" />
          <TextField fullWidth margin="normal" label="Username" name="username" value={formData.username} onChange={handleChange} error={!!errors.username} helperText={errors.username || 'Lowercase letters, numbers, and dots only'} required data-id="element-2895" data-mp="4lpv41z8vvcc" />
          <TextField fullWidth margin="normal" label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} required data-id="element-2896" data-mp="1sjqvq4t5uv94" />
          <TextField fullWidth margin="normal" label="Mobile Number" name="mobile" value={formData.mobile} onChange={handleChange} error={!!errors.mobile} helperText={errors.mobile || 'Must start with +92 and be 10 digits after +92'} placeholder="+92 300 1234567" required data-id="element-2897" data-mp="2g8jndp2r2gxx" />
          <FormControl fullWidth margin="normal" data-id="element-2898" data-mp="2z25n0ysfmmdn">
            <InputLabel id="branch-code-label" data-id="element-2899" data-mp="2u8kx7u0zsypj">
              Branch Code (Optional)
            </InputLabel>
            <Select labelId="branch-code-label" name="branchCode" value={formData.branchCode} onChange={handleChange} label="Branch Code (Optional)" data-id="element-2900" data-mp="31m296665nbzl">
              <MenuItem value="" data-id="element-2901" data-mp="3bl16wtpdtn7t">
                <em data-id="element-2902" data-mp="3giknw1dgse3g">None</em>
              </MenuItem>
              {BRANCH_CODES.map(code => <MenuItem key={code} value={code} data-id="element-2903" data-mp="3kk69cb42e0p9">
                  {code}
                </MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth margin="normal" label="Settlement Account (Optional)" name="settlementAccount" value={formData.settlementAccount} onChange={handleChange} data-id="element-2904" data-mp="3pgmxv6f6eh2v" />
          <TextField fullWidth margin="normal" label="Designation (Optional)" name="designation" value={formData.designation} onChange={handleChange} data-id="element-2905" data-mp="839bt6fy8n5e" />
        </Box>
      </Box>
      <Box sx={{
      position: 'sticky',
      bottom: 0,
      backgroundColor: 'background.paper',
      p: 3,
      borderTop: '1px solid #E5E7EB'
    }} data-id="element-2906" data-mp="td6j6x1lmp2v">
        <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2
      }} data-id="element-2907" data-mp="12dux7rqzspfr">
          <Button variant="outlined" onClick={onClose} data-id="element-2908" data-mp="epbojb416an5">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} data-id="element-2909" data-mp="9q8j3pk522en">
            {user ? 'Save Changes' : 'Create User'}
          </Button>
        </Box>
      </Box>
    </Drawer>;
};
export default UserFormDrawer;